<template>
  <v-menu
    v-if="canUserSeeTasks && !mobile"
    v-model="menu"
    :close-on-content-click="false"
    location="bottom right"
    max-width="350"
  >
    <template #activator="{ props: ActivatorProps }">
      <v-btn
        v-bind="ActivatorProps"
        class="pa-0"
        :loading="isFetchingTasksInitial"
      >
        <v-badge
          :color="tasks.length > 0 ? 'warning' : 'primary'"
          :content="`${tasks.length}`"
        >
          <v-icon :color="doActiveApiPolling ? undefined : 'warning'" start>
            far fa-download
          </v-icon>
        </v-badge>
      </v-btn>
    </template>

    <DeepFlowTasksList :tasks="tasks" @click:item="onTaskClick" />
    <DeepFlowIFrameDialog
      v-if="currentTask && dialogDeepFlowIFrame"
      v-model="dialogDeepFlowIFrame"
      closable
      :iframe-src="taskIFrameUrl"
    />
  </v-menu>
</template>

<script lang="ts" setup>
import { toast } from 'vue-sonner'
import { useDisplay } from 'vuetify'
import { isLocalOrDevEnv } from '@/utils/helpers/env.ts'
import { useDeepBoxAdminStore } from '@/stores/deepbox/admin/admin'
import { useApi } from '@/composables/api'
import DeepFlowIFrameDialog from '@/components/deepflow/DeepFlowIFrameDialog.vue'
import DeepFlowTasksList from '@/components/deepflow/DeepFlowTasksList.vue'
import type { Task } from '@deepcloud/api-deep-o'

const props = defineProps({
  organizationId: {
    type: String,
    default: undefined,
  },
  typeId: {
    type: String,
    default: undefined,
  },
  boxId: {
    type: String,
    default: undefined,
  },
})

const { deepOAPI } = useApi()

const menu = ref(false)
const tasks = ref<Task[]>([])

const isFetchingTasksInitial = ref(true)

async function fetchUserTasks() {
  try {
    const { data } = await deepOAPI.value.pengRuntimeCurrentUser.get({
      companyId: props.organizationId,
      deepBoxNodeId: props.typeId,
      boxNodeId: props.boxId,
    })
    tasks.value = data.tasks
  } catch (e) {
    console.error(e)
  } finally {
    isFetchingTasksInitial.value = false
  }
}

const deepBoxAdminStore = useDeepBoxAdminStore()
const canAdminAccess = computed(
  () => deepBoxAdminStore.adminBox?.boxPolicy?.canAdminAccess || false,
)

const { xs, mobile } = useDisplay()
const canUserSeeTasks = computed(
  () => !xs.value && isLocalOrDevEnv() && canAdminAccess,
)

const hasAllNeededParamsCallTheAPI = computed(
  () => props.organizationId && props.typeId && props.boxId,
)

const currentPollingId = ref<ReturnType<typeof setInterval> | null>(null)
const doActiveApiPolling = useLocalStorage('activeDeepFlowTaskPolling', false)

function clearCurrentInterval() {
  if (currentPollingId.value) {
    clearInterval(currentPollingId.value)
    currentPollingId.value = null
  }
}

function startActivePolling() {
  clearCurrentInterval()
  currentPollingId.value = setInterval(() => {
    fetchUserTasks()
  }, 5000)
}

watch(
  () => hasAllNeededParamsCallTheAPI.value,
  (newValue) => {
    if (newValue) {
      if (canUserSeeTasks.value) {
        fetchUserTasks()
        if (doActiveApiPolling.value) {
          startActivePolling()
        }
      } else {
        clearCurrentInterval()
      }
    }
  },
  { immediate: true },
)

watch(
  () => doActiveApiPolling.value,
  (newValue) => {
    if (newValue) {
      if (canUserSeeTasks.value) {
        fetchUserTasks()
        if (doActiveApiPolling.value) {
          clearCurrentInterval()
          startActivePolling()
        }
      } else {
        clearCurrentInterval()
      }
      toast.success('Active DeepFlow API polling enabled')
    } else {
      clearCurrentInterval()
      toast.warning('Active DeepFlow API polling disabled')
    }
  },
)

onUnmounted(() => clearCurrentInterval())

const currentTask = ref<Task | undefined>(undefined)
const dialogDeepFlowIFrame = ref(false)

function onTaskClick({ task }) {
  currentTask.value = task
  dialogDeepFlowIFrame.value = true
}

const taskIFrameUrl = computed(() => {
  if (!currentTask.value) return
  return `${import.meta.env.VITE_DEEPFLOW_FRONTEND_BASE_URL}/runtime/deepbox/${
    currentTask.value?.definitionId
  }/-1?orgId=${currentTask.value?.companyId}&typeId=${
    currentTask.value?.deepBoxNodeId
  }&boxId=${currentTask.value?.boxNodeId}& &instanceId=${
    currentTask.value?.instanceId
  }&environment=DeepBoxInteractive`
})
</script>
